import { create } from '@bufbuild/protobuf'
import {
  DateSchema,
  type Date as ProtoDate,
} from '@ebalance/proto/google/type/date_pb'

import { now as serverNow } from './clock'

/**
 * 日時整形・パース・演算の canonical module。
 *
 * 共有層に散在していた日時 formatter / parser / 演算ヘルパーを集約する。 各 formatter は
 * **`Date` を受ける** (= proto `Timestamp` を扱う呼び出し側は `timestampDate` で変換して
 * から渡す)。 出力フォーマットは用途ごとに異なるため
 * 統一せず、 関数名で表現する。
 */

/**
 * JST は固定 +9h (DST なし)。実行環境のタイムゾーンに依存させないための offset
 * (`hooks/useTodayJst` / `models/weather/time.ts` が個別に持っていた同値の canonical 化)。
 * `new Date(d.getTime() + JST_OFFSET_MS)` の `getUTC*` が JST の壁時計値になる。
 */
export const JST_OFFSET_MS = 9 * 60 * 60 * 1000

// `Date` は 2 つの役割で使い分ける:
//
// - **カレンダー日 `Date`** = 「暦日 (Y/M/D) だけを運ぶ `Date`」 (= ローカル 0:00)。
//   `parseIsoDate` / `fromProtoDate` / `parseLocalYmd` が作り、 `formatDateValue` /
//   `toProtoDate` が読む、 この app 共通の受渡日・対象日の表現。 時刻部分に意味は無く、
//   取り出しは常にローカル getter (`getFullYear` 等) で行う (= 生成と対称)。
// - **絶対時刻 `Date`** (proto `Timestamp` 由来 / `./clock` の `now()`) は瞬間を表す。
//
// 業務上の壁時計 (コマ境界・GC・開場時刻) は JST で定義されるため、 両者を跨ぐ変換は
// 端末 TZ ではなく JST を軸に行う (`jstWallClockToInstant` / `jstYmd`)。
//
// 「いつ」 の取得元 (サーバー由来の `now()`) と 「どの暦日 / どのコマか」 の軸
// (JST) は直交する。 本 module の `now` 既定引数は前者、 変換ロジックは後者を担う。

/**
 * カレンダー日 + JST 壁時計の時刻 → 絶対時刻。
 * 例: 2026-05-26 の JST 14:00 → `2026-05-26T05:00:00Z` (端末 TZ に依存しない)。
 *
 * `dateToKomaNumber` (絶対時刻 → JST のコマ番号) の逆向きにあたる変換で、 同じ
 * [[JST_OFFSET_MS]] 固定 offset を使う (JST は DST 無しの +9h 固定)。
 * hour / minute は 0-23 / 0-59 の範囲外でも `Date.UTC` の桁上がりに従って日を跨ぐ
 * (= GC 「コマ開始の 1 時間前」 が前日に跨る計算を呼び出し側で場合分けしない)。
 */
export function jstWallClockToInstant(
  calendarDate: Date,
  hour: number,
  minute: number,
): Date {
  return new Date(
    Date.UTC(
      calendarDate.getFullYear(),
      calendarDate.getMonth(),
      calendarDate.getDate(),
      hour,
      minute,
    ) - JST_OFFSET_MS,
  )
}

/**
 * JST の「今日」 をカレンダー日 `Date` で返す。 端末 TZ が JST 以外だと
 * `new Date(now.getFullYear(), now.getMonth(), now.getDate())` は JST の暦日と最大
 * 1 日ズレる (= 受渡日の既定値が前日/翌日になる) ため、 「今日」 を起点にする画面は
 * これを使う。
 *
 * 「いつ」 の取得元はサーバー由来 (既定引数は `~/models/clock` の `now()`)、
 * 「どの暦日か」 の軸は JST — 2 つは直交する。
 */
export function startOfTodayJst(now: Date = serverNow()): Date {
  const { year, month, day } = jstYmd(now)
  return new Date(year, month - 1, day)
}

/**
 * `<input type="datetime-local">` の値 ("YYYY-MM-DDTHH:mm") を **JST 壁時計**として
 * 絶対時刻に変換する。 形式不正は `undefined`。
 *
 * `new Date(value)` は datetime-local を端末 TZ の壁時計として解釈するが、 入力欄に
 * 表示されるのは業務上の JST 壁時計 (= 同じ画面の GC・コマ帯と同じ軸) なので、
 * 端末 TZ で解釈すると非 JST 端末で最大 9 時間ズレた瞬間になる。
 */
export function parseJstDateTimeLocal(value: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value)
  if (m === null) return undefined
  const [, y, mo, d, h, min, sec] = m
  const year = Number(y)
  const month = Number(mo)
  const day = Number(d)
  const hour = Number(h)
  const minute = Number(min)
  const second = sec === undefined ? 0 : Number(sec)
  // 桁数だけ合った範囲外の値 (`T99:00` / `T12:99` / `-02-31`) を弾く。 正規表現を
  // 通っただけの値を `Date.UTC` に渡すと桁上がりして**別の有効な日時**になるため、
  // 置換前の `new Date(value)` が Invalid Date として弾いていた壊れた入力が、
  // 「past か / GC 後か」 の判定へそのまま流れてしまう。 月末日の判定は
  // `Date.UTC` の桁上がりを利用し、 JST 壁時計へ戻して入力と一致するかで見る
  // (= うるう年を自前で持たない)。
  if (month < 1 || month > 12) return undefined
  if (day < 1 || day > 31) return undefined
  if (hour > 23 || minute > 59 || second > 59) return undefined
  // `Date.UTC` は year 0〜99 を 1900 年台へ補正する (= "0050-..." が 1950 年になる)。
  // 入力欄に `min` が無く 4 桁年の `0050-01-01T00:00` は正当な datetime-local 値
  // なので、 補正を避けて `setUTCFullYear` で組む (補正されると下の暦日一致検査が
  // 必ず外れ、 有効な入力を形式不正として捨てる)。
  const utc = new Date(0)
  utc.setUTCFullYear(year, month - 1, day)
  utc.setUTCHours(hour, minute, second, 0)
  const instant = new Date(utc.getTime() - JST_OFFSET_MS)
  if (Number.isNaN(instant.getTime())) return undefined
  const ymd = jstYmd(instant)
  if (ymd.year !== year || ymd.month !== month || ymd.day !== day) {
    return undefined
  }
  return instant
}

/** 絶対時刻 → **JST 壁時計**の datetime-local 値 ("YYYY-MM-DDTHH:mm")。 [[parseJstDateTimeLocal]] の逆向き。 */
export function formatJstDateTimeLocal(d: Date): string {
  const { year, month, day } = jstYmd(d)
  const { hour, minute } = jstHms(d)
  const pad2 = (n: number): string => String(n).padStart(2, '0')
  return `${String(year).padStart(4, '0')}-${pad2(month)}-${pad2(day)}T${pad2(hour)}:${pad2(minute)}`
}

/**
 * テロップバーの日時ラベル (`8/5(水) 14:29` / en: `Wed, 8/5 14:29`)。
 *
 * design `design/js/include-nav.js` の `_tfFmtDate` + `_tfFmtTime` 相当。曜日と
 * 月日の並びは locale 依存 (`Intl`) だが、**基準は常に JST 固定** — 端末 TZ が
 * 異なっても電力業務の壁時計 (JST) を表示する (`dateToKomaNumber` と同じ判断)。
 */
export function formatTickerDateTime(now: Date, locale: string): string {
  const date = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'numeric',
    timeZone: 'Asia/Tokyo',
    weekday: 'short',
  }).format(now)
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  }).format(now)
  return `${date} ${time}`
}

/** `YYYY-MM-DD` をローカル日付として parse。 不正/未指定なら `null`。 */
export function parseLocalYmd(s: string | null | undefined): Date | null {
  if (s == null || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
  const [y, m, d] = s.split('-').map((v) => parseInt(v, 10))
  const dt = new Date(y, m - 1, d)
  return Number.isNaN(dt.getTime()) ? null : dt
}

/** ローカル日付に `n` 日加算した新しい `Date` を返す (元は破壊しない)。 */
export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
}

/** 2 つの `Date` が同一ローカル日 (年月日一致) か。 */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** 表示言語に合わせたローカル日付。 日本語は `YYYY/MM/DD`、 英語は `YYYY-MM-DD`。 */
export function formatDateDisplay(d: Date, language = 'ja'): string {
  if (language.startsWith('en')) return formatDateValue(d)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}/${m}/${day}`
}

/** `YYYY-MM-DD` (= date input value 用、 ローカルタイムゾーン)。 */
export function formatDateValue(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** `YYYY-MM-DD` (= date input value 用、 JST 基準)。 */
export function formatDateValueJst(d: Date = serverNow()): string {
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
  })
  const parts = fmt.formatToParts(d)
  const y = parts.find((p) => p.type === 'year')?.value ?? '0'
  const m = parts.find((p) => p.type === 'month')?.value ?? '0'
  const day = parts.find((p) => p.type === 'day')?.value ?? '0'
  return `${y}-${m}-${day}`
}

/**
 * 絶対時刻 → **JST の暦日**の `YYYY/MM/DD` (= お知らせの公開日の表示用)。
 * 書式は端末ローカル基準の [[formatDateDisplay]] に揃え、 軸だけ JST にする
 * (公開日は配信側が JST で付与する instant で、 非 JST 端末で前日に見えると
 * 告知の日付と食い違うため)。
 */
export function formatJstDateDisplay(d: Date): string {
  const { year, month, day } = jstYmd(d)
  return `${year}/${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`
}

/**
 * `YYYY-MM-DD` → JS `Date` (= ローカルタイムゾーンの 0:00)。 形式不正 / 空文字は
 * `undefined`。 date input value や URL クエリの ISO 文字列を `Date` に戻す唯一の
 * 経路 (= `formatDateValue` の逆)。 呼び出し側で `split('-')` を手書きしない。
 */
export function parseIsoDate(iso: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return undefined
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** JS `Date` を google.type.Date (= year/month/day) へ変換する。 */
export function toProtoDate(d: Date): ProtoDate {
  return create(DateSchema, {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
  })
}

/**
 * google.type.Date (= year/month/day) を JS `Date` (= ローカルタイムゾーンの 0:00)
 * へ変換する (= `toProtoDate` の逆)。
 */
export function fromProtoDate(d: ProtoDate): Date {
  return new Date(d.year, d.month - 1, d.day)
}

/**
 * 絶対時刻 → **JST 壁時計**の `M/D HH:MM` (= 年なし・月日ゼロ埋めなし、 一覧テーブルの
 * 監査セル用)。 [[formatJstMonthDayHm]] と同じ出力 (#6074)。
 */
export function formatMonthDayTime(d: Date): string {
  return formatJstMonthDayHm(d)
}

/**
 * 絶対時刻 → **JST 壁時計**の `YYYY/MM/DD HH:MM` (= 監査情報パネル用)。
 * [[formatJstDateTimeHm]] と同じ出力 (#6074)。
 */
export function formatDateTimeFull(d: Date): string {
  return formatJstDateTimeHm(d)
}

/**
 * 絶対時刻 → **JST 壁時計**の `HH:MM:SS` (= 同時同量画面の「託送実績データ 最終更新」表示用)。
 * [[formatJstTimeHms]] と同じ出力 (#6074)。
 *
 * 秒まで出すのは design (`design/balance/bg.html` の `#bg-last-updated` =
 * `--:--:--`) に合わせるため。当日監視では分単位だと「今入った」のか
 * 「30 分前から止まっている」のかが読み取りにくい。
 */
export function formatTimeOfDay(d: Date): string {
  return formatJstTimeHms(d)
}

/**
 * 絶対時刻 → JST の時刻 (時 / 分 / 秒)。 [[jstYmd]] の時刻版で、 同じく Intl 経由で
 * tzdata 依存を避ける。
 *
 * `hourCycle: 'h23'` は 0 時を `24` と出す h24 サイクルを避けるため
 * (`hour12` を併記すると `hourCycle` は無視される仕様なので指定しない)。
 */
function jstHms(d: Date): { hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Asia/Tokyo',
  }).formatToParts(d)
  const part = (type: Intl.DateTimeFormatPartTypes): number =>
    parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10)
  return { hour: part('hour'), minute: part('minute'), second: part('second') }
}

/**
 * 絶対時刻 → **JST 壁時計**の `HH:MM` (= コマ別 GC 締切・板の空容量更新時刻の表示用)。
 *
 * コマ・GC は JST 壁時計で定義される業務概念なので、 判定 (`dateToKomaNumber` /
 * `calcGcAtForKoma`) と同じ軸で表示する。
 * 秒なしの `HH:MM` 専用 helper (秒付きは [[formatJstTimeHms]])。
 */
export function formatJstTimeHm(d: Date): string {
  const { hour, minute } = jstHms(d)
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/**
 * 絶対時刻 → **JST 壁時計**の `M/D` (= 年なし・月日ゼロ埋めなし)。 スポット入札の
 * 承認申請確認 modal の入札締切表示用 (issue #8320、 Des 確定 2026-09-18。
 * [[formatJstDateTimeHm]] は
 * 日跨ぎ混同防止のため年ありが規約 (AC-BIN-047) だが、 スポットの入札締切表示は
 * 「入札締切 4/23 10:00」 の形で年を出さない設計判断のため別 helper にする)。
 * 入札締切は JST 壁時計の業務概念。
 */
export function formatJstMonthDay(d: Date): string {
  const { month, day } = jstYmd(d)
  return `${month}/${day}`
}

/**
 * 絶対時刻 → **JST 壁時計**の `HH:MM:SS` (= 板の約定時刻の表示用)。 秒まで出す理由は
 * design `design/bidding/intraday.html` の約定履歴に合わせるため (約定は秒単位で並ぶ)。
 * 軸の考え方は [[formatJstTimeHm]] と同じ。
 */
export function formatJstTimeHms(d: Date): string {
  const { hour, minute, second } = jstHms(d)
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`
}

/**
 * 絶対時刻 → **JST 壁時計**の `M/D HH:MM` (= スポット入札の予約入札バッジが出す
 * 「受付開始 M/D HH:MM〜」 用。 spec `bidding/spot-bidding.md` §市場フェーズ の表記)。
 *
 * バッジは幅の限られた page-header に収めるため年を落とす。 受付開始は受渡日の 11 日前で、
 * 画面に出ている受渡日と 2 週間以内に収まるので年の取り違えは起きない。
 * 受付開始・締切は同じ画面のフェーズ判定 ([[calcSpotPhase]]) と同じ JST 壁時計で読む
 * 業務値なので、 端末ローカル getter を使わない。
 * [[formatMonthDayTime]] は本 helper に委譲していて出力は同じ。
 */
export function formatJstMonthDayHm(d: Date): string {
  const { month, day } = jstYmd(d)
  return `${month}/${day} ${formatJstTimeHm(d)}`
}

/**
 * 絶対時刻 → **JST 壁時計**の `YYYY/MM/DD HH:MM:SS` (= 気象情報バーの発表時刻表示用、
 * AC-DFC-008d)。 [[jstYmd]] + [[jstHms]] を合成する。
 *
 * 発表時刻は JMA が JST で定義する発表スケジュール (`jma-weather-data.md` §1、
 * 05/11/17 時等) に基づく業務概念なので、 端末ローカル TZ ではなく JST 軸で表示する。
 * 端末ローカル基準の `toLocaleString` を
 * 直書きすると非 JST 端末で発表時刻がズレて見える。
 */
export function formatJstDateTimeFull(d: Date): string {
  const { year, month, day } = jstYmd(d)
  const { hour, minute, second } = jstHms(d)
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  const hh = String(hour).padStart(2, '0')
  const min = String(minute).padStart(2, '0')
  const sec = String(second).padStart(2, '0')
  return `${year}/${mm}/${dd} ${hh}:${min}:${sec}`
}

/**
 * 絶対時刻 → **JST 壁時計**の `YYYY/MM/DD HH:MM` (= 時間前入札の監査情報・GC 日時の
 * 表示用、 AC-BIN-047)。 [[formatJstDateTimeFull]] の秒なし版。
 *
 * 書式が `yyyy/MM/dd HH:mm` 固定なのは spec `bidding/intraday-bidding.md` §9 監査情報
 * の既定 (「MM/dd 単独は日跨ぎ運用での混同事故防止のため不可」) による。
 * 入札の作成 / 更新日時は同じ
 * 画面のコマ・GC・受渡日の判定と同じ JST 壁時計で読む業務値なので、 非 JST 端末で
 * ズレる `toLocaleString` 直書きや端末ローカル getter を使わない。 [[formatDateTimeFull]] /
 * [[formatDateTimeLocaleJa]] は本 helper に委譲していて出力は同じ。
 */
export function formatJstDateTimeHm(d: Date): string {
  const { year, month, day } = jstYmd(d)
  const { hour, minute } = jstHms(d)
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  const hh = String(hour).padStart(2, '0')
  const min = String(minute).padStart(2, '0')
  return `${year}/${mm}/${dd} ${hh}:${min}`
}

/**
 * 絶対時刻 → **JST 壁時計**の `MM/DD HH:MM` (= 時間前入札の取下予定日時の表示用、
 * AC-BIN-003)。 **月日をゼロ埋めする**点が、 同じく年を持たない [[formatJstMonthDayHm]] /
 * [[formatMonthDayTime]] (`M/D HH:MM`、 ゼロ埋めなし) と違う。 年を持たない点が
 * [[formatJstDateTimeHm]] と違う — spec は「表示形式は
 * MM/dd HH:mm とし、 当日分と翌日分を判別できるよう日付を必ず併記する」 とだけ定め、
 * 年跨ぎの混同事故を心配する運用ではないため (`docs/specs/bidding/
 * intraday-bidding.md` AC-BIN-003)。 軸の考え方は [[formatJstTimeHm]] と同じ。
 */
export function formatJstMonthDayTime(d: Date): string {
  const { month, day } = jstYmd(d)
  const { hour, minute } = jstHms(d)
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  const hh = String(hour).padStart(2, '0')
  const min = String(minute).padStart(2, '0')
  return `${mm}/${dd} ${hh}:${min}`
}

/**
 * 絶対時刻 → **JST 壁時計**の `YYYY-MM-DD HH:MM` (= CSV ダウンロードの基準時点表示用)。
 *
 * ハイフン区切りなのは design `admin/uploads/index.html` の日時表記に合わせる
 * ため (= 監査情報パネルのスラッシュ区切り `formatDateTimeFull` とは別用途)。
 */
export function formatDateTimeHyphen(d: Date): string {
  const { year, month, day } = jstYmd(d)
  const { hour, minute } = jstHms(d)
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  const hh = String(hour).padStart(2, '0')
  const min = String(minute).padStart(2, '0')
  return `${year}-${mm}-${dd} ${hh}:${min}`
}

/** `YYYY/MM/DD 0:00~24:00` (= 計画対象日の全日レンジ表示用)。 proto `Date` 相当の `{year, month, day}` を受ける。 */
export function formatTargetDateRange(
  d: { year: number; month: number; day: number } | undefined,
): string {
  if (!d) return '—'
  return `${formatDateDisplay(new Date(d.year, d.month - 1, d.day))} 0:00~24:00`
}

/**
 * JST 基準の「今日」に対する targetDate のオフセット (日単位)。 undefined は
 * 「未取得」 を意味し、 いずれの日付判定も false に落とす前提で undefined を
 * 返す (呼び出し側は toProtoDate / wfCase.targetDate を渡す)。
 *
 * 内部は targetDate の `{year, month, day}` を Intl の JST カレンダー日 (year/month/day) と
 * 直接比較する (`Date` 経由の演算は DST 影響を
 * 受けないが `JST=UTC+9 固定` の tzdata 依存があると壊れうるので Intl 経由が安全)。
 *
 * `~/models/planSubmission/targetDate.ts` の `timeframeForTargetDate`
 * (対象日→OCCTO 提出区分の導出、procurement-create-dialog-section-target-date-approval
 * 設計 Decision #5) も同じ「対象日の暦日 vs JST の今日」比較のために本関数を使う
 * (端末ローカル壁時計で基準日を丸めると非 JST 端末で誤分類する)。
 */
export function targetDateJstOffsetDays(
  targetDate: { year: number; month: number; day: number } | undefined,
  now: Date = serverNow(),
): number | undefined {
  if (targetDate === undefined) return undefined
  const nowJst = jstYmd(now)
  const nowUtc = Date.UTC(nowJst.year, nowJst.month - 1, nowJst.day)
  const targetUtc = Date.UTC(
    targetDate.year,
    targetDate.month - 1,
    targetDate.day,
  )
  return Math.round((targetUtc - nowUtc) / (24 * 60 * 60 * 1000))
}

/**
 * `Date` (= 絶対時刻) を JST の暦日 (year, month, day) に写す。 Intl 経由で tzdata
 * 依存を回避する。 「今が JST の何日か」 を端末 TZ に依存せず求める canonical。
 */
export function jstYmd(d: Date): { year: number; month: number; day: number } {
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
  })
  const parts = fmt.formatToParts(d)
  const year = parseInt(parts.find((p) => p.type === 'year')?.value ?? '0', 10)
  const month = parseInt(
    parts.find((p) => p.type === 'month')?.value ?? '0',
    10,
  )
  const day = parseInt(parts.find((p) => p.type === 'day')?.value ?? '0', 10)
  return { year, month, day }
}

/**
 * targetDate が JST の「今日」 か。 undefined (未取得) は false。 needs 判定は
 * URL segment / WorkflowSection 値ではなく `wfCase?.targetDate` (proto Date)
 * を渡す — 「当日実績を出す / GC を効かせる」 のは URL 上のセクション名ではなく
 * 実カレンダー日付が今日のときであるため (backend valueobject.IsTargetDateToday と対応)。
 */
export function isTargetDateTodayInJst(
  targetDate: { year: number; month: number; day: number } | undefined,
  now: Date = serverNow(),
): boolean {
  return targetDateJstOffsetDays(targetDate, now) === 0
}

/**
 * targetDate が JST の「今日 + 1 (明日)」 か。 undefined (未取得) は false。
 */
export function isTargetDateTomorrowInJst(
  targetDate: { year: number; month: number; day: number } | undefined,
  now: Date = serverNow(),
): boolean {
  return targetDateJstOffsetDays(targetDate, now) === 1
}

/**
 * targetDate が JST の「今日 + 2 (翌々日)」 以降か。 undefined (未取得) は false。
 * 気象粒度案内 (AC-WD-005e) の判定に使う — VPFD の 3 時間予報は明日 24 時まで
 * のため、対象日が翌々日以降だと時間帯別粒度が構造的に空になる旨を案内する
 * (backend valueobject との対応は無し、frontend 表示専用の判定)。
 *
 * URL の WorkflowSection ではなく `wfCase.targetDate` (proto Date) で判定する
 * (`isTargetDateTodayInJst` と同軸)。
 * WF ケースは断面属性を持たず対象日のみ持つため、URL segment 由来のラベルと
 * 対象日オフセットは 1:1 対応しない (2026-08-26 PdM 判断)。
 */
export function isTargetDateAtOrAfterDayAfterTomorrowInJst(
  targetDate: { year: number; month: number; day: number } | undefined,
  now: Date = serverNow(),
): boolean {
  const offset = targetDateJstOffsetDays(targetDate, now)
  return offset !== undefined && offset >= 2
}

/**
 * targetDate が JST の「今日」より前 (過去日) か。 undefined (未取得) は false。
 * 過去日は `docs/specs/conventions.md` §GC ロックにより全コマ編集不可 (受渡
 * 終了済み = 全 GC 済み) — backend `valueobject.GcKomaForDate` が過去日で
 * `Some(MaxKomaNumber)` (=48、全コマロック) を返すのと対応する。
 */
export function isTargetDateBeforeTodayInJst(
  targetDate: { year: number; month: number; day: number } | undefined,
  now: Date = serverNow(),
): boolean {
  const offset = targetDateJstOffsetDays(targetDate, now)
  return offset !== undefined && offset < 0
}

/**
 * 2 つの google.type.Date (year/month/day) が同一日か。 どちらかが undefined
 * なら false。 §6 確定履歴から取込ダイアログの候補絞り込み (danmen drift 監査
 * hit-01: 断面フィルタ撤去に伴い、同一 target_date 内の候補へ絞る) に使う。
 */
export function isSameProtoDate(
  a: { year: number; month: number; day: number } | undefined,
  b: { year: number; month: number; day: number } | undefined,
): boolean {
  if (a === undefined || b === undefined) return false
  return a.year === b.year && a.month === b.month && a.day === b.day
}

/**
 * 絶対時刻 → **JST 壁時計**の `YYYY/MM/DD HH:MM` (= ケースバックバーの完了日時用)。
 * 旧実装の `toLocaleString('ja-JP', 2-digit)` と同じ表記で、 [[formatJstDateTimeHm]] に委譲する。
 */
export function formatDateTimeLocaleJa(d: Date): string {
  return formatJstDateTimeHm(d)
}
